/**
 * PaymentGuard - Componente centralizado de verificação de pagamento
 * 
 * Verifica se o tenant está com pagamento pendente (past_due) ou suspenso.
 * Se estiver em rota permitida (/tenant/plans), renderiza children normalmente.
 * Caso contrário, exibe o paywall de suspensão/inadimplência.
 * 
 * Usado em todos os layouts (TenantLayout, CrmLayout, ModularLayout) para
 * garantir controle uniforme de acesso por pagamento.
 * 
 * @author Sistema
 * @version 1.1.0
 *
 * CHANGELOG:
 *   v1.2.0 (2026-10-01) — Bypass do tenant master (kind='wl_internal'):
 *     não bloqueia por pagamento e não exibe banner de cobrança.
 *   v1.1.0 (2026-07-02) — Novo branch `awaiting_first_payment`: tenants de
 *     Planos 2.0 (integracao_v1) que contrataram um plano pré-pago mas ainda
 *     não pagaram a primeira fatura passam a ficar bloqueados em todas as
 *     rotas exceto `/tenant/plans`. Trial e upgrades subsequentes NÃO são
 *     afetados. Comportamento espelha o legado (`subscription_status='past_due'`).
 *   v1.0.0 — Versão inicial.
 */

import { ReactNode, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { getStorageUrl } from '@/lib/storage';
import { UserProfileDialog } from '@/components/tenant/UserProfileDialog';
import { TenantSwitcher } from '@/components/tenant/TenantSwitcher';
import { TenantSuspendedPaywall } from '@/components/tenant/TenantSuspendedPaywall';
import { TenantBillingBanner } from '@/components/tenant/TenantBillingBanner';
import { useTenantBillingSelfOverview } from '@/hooks/tenant/useTenantPlansV2';
import { LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

interface PaymentGuardProps {
  children: ReactNode;
}


/**
 * Extrai as iniciais do nome ou email do usuário
 * @param name - Nome completo ou email
 * @returns Iniciais em maiúsculo (máximo 2 caracteres)
 */
const getInitials = (name: string): string => {
  if (!name) return 'U';
  if (name.includes('@')) {
    return name.split('@')[0].charAt(0).toUpperCase();
  }
  return name
    .split(' ')
    .filter(word => word.length > 0)
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export function PaymentGuard({ children }: PaymentGuardProps) {
  const { user, signOut, currentTenant, availableTenants, switchTenant, profile, refreshProfile } = useAuth();
  const location = useLocation();
  const { t } = useTranslation('sidebar');

  // Estado do modal de perfil (necessário para header mínimo do paywall)
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  // Overview do gateway novo (integracao_v1 / uaizy) — Fase D
  const { data: overview } = useTenantBillingSelfOverview();
  const isIntegracaoV1 =
    !!overview?.has_binding && (overview?.gateway_slug ?? 'uaizy') === 'uaizy';

  // Rotas permitidas mesmo com tenant suspenso ou inadimplente
  const allowedSuspendedRoutes = ['/tenant/plans'];
  const isAllowedRoute = allowedSuspendedRoutes.some(route => location.pathname.startsWith(route));

  // Branch novo (integracao_v1): bloqueia em suspended / past_due expirado /
  // canceled / aguardando 1º pagamento (v1.1.0).
  let isBlockedByIntegracaoV1 = false;
  let isAwaitingFirstPayment = false;
  if (isIntegracaoV1 && overview) {
    if (
      overview.status === 'suspended' ||
      (overview.status === 'past_due' && (overview.dunning_days_left ?? 0) <= 0) ||
      overview.status === 'canceled'
    ) {
      isBlockedByIntegracaoV1 = true;
    }
    // v1.1.0 — Gate de 1ª contratação pré-paga sem pagamento confirmado.
    // A flag `awaiting_first_payment` vem do backend (RPC v1.7.0), já
    // considerando trial ativo e histórico de cobranças pagas.
    if (overview.awaiting_first_payment === true) {
      isBlockedByIntegracaoV1 = true;
      isAwaitingFirstPayment = true;
    }
  }

  // Branch legado (Stripe): respeita os campos antigos quando não há binding novo
  const isPastDue = !isIntegracaoV1 && currentTenant?.subscription_status === 'past_due';
  const isSuspended = !isIntegracaoV1 && currentTenant?.status === 'suspended';

  /**
   * Executa logout com redirecionamento via window.location
   */
  const handleLogout = () => {
    signOut(() => {
      window.location.href = '/auth';
    });
  };

  /**
   * Callback para trocar de empresa - navega para primeira ativa disponível
   */
  const handleSwitchTenant = () => {
    const activeTenant = availableTenants.find(t => t.status === 'active' && t.id !== currentTenant?.id);
    if (activeTenant) {
      switchTenant(activeTenant.id);
    }
  };

  // v1.2.0 — Tenant master do White Label (kind='wl_internal') tem bypass
  // total de cobrança: nunca é bloqueado nem exibe banner de pagamento.
  const isMasterTenant = currentTenant?.kind === 'wl_internal';
  if (isMasterTenant) {
    return <>{children}</>;
  }

  // Renderiza children (com banner de trial/past_due) quando nada bloqueia
  const shouldBlock = isSuspended || isPastDue || isBlockedByIntegracaoV1;
  if (!shouldBlock || isAllowedRoute) {
    return (
      <>
        <TenantBillingBanner />
        {children}
      </>
    );
  }

  // Determinar o motivo correto para exibir no paywall.
  // v1.1.0 — quando o bloqueio é por 1ª cobrança pendente, usa o novo motivo
  // `awaiting_first_payment` (copy diferenciada dentro do PaymentFailedLayout).
  const effectiveReason:
    | 'multiple_free_tenants'
    | 'trial_expired'
    | 'payment_failed'
    | 'awaiting_first_payment'
    | null =
    isAwaitingFirstPayment
      ? 'awaiting_first_payment'
      : isBlockedByIntegracaoV1 || isPastDue
        ? 'payment_failed'
        : (currentTenant?.suspension_reason as 'multiple_free_tenants' | 'trial_expired' | 'payment_failed' | null);



  // Renderiza paywall com header mínimo
  return (
    <TooltipProvider>
      <div className="min-h-screen flex flex-col">
        {/* Header mínimo com opções de navegação */}
        <header className="h-14 bg-card flex items-center justify-between px-6 border-b">
          <TenantSwitcher
            availableTenants={availableTenants}
            currentTenant={currentTenant}
            onSwitch={switchTenant}
          />
          
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="compact" />
            <ThemeToggle />
            <Tooltip>
              <TooltipTrigger asChild>
                <Avatar 
                  className="h-9 w-9 cursor-pointer hover:ring-2 hover:ring-primary/20 transition-all"
                  onClick={() => setProfileDialogOpen(true)}
                >
                  <AvatarImage 
                    src={getStorageUrl(profile?.avatar_url)} 
                    alt={profile?.full_name || 'Avatar'} 
                  />
                  <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                    {getInitials(profile?.full_name || user?.email || '')}
                  </AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="text-xs">{user?.email}</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={handleLogout} className="h-9 w-9 rounded-full">
                  <LogOut className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{t('tooltips.logout')}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </header>
        
        {/* Paywall de suspensão/inadimplência com layout diferenciado por motivo */}
        <TenantSuspendedPaywall 
          tenantName={currentTenant!.name}
          suspensionReason={effectiveReason}
          onSwitchTenant={availableTenants.some(t => t.status === 'active' && t.id !== currentTenant!.id) ? handleSwitchTenant : undefined}
        />
        
        {/* Modal de Perfil do Usuário */}
        <UserProfileDialog
          open={profileDialogOpen}
          onOpenChange={setProfileDialogOpen}
          userId={user?.id || ''}
          userEmail={user?.email || ''}
          currentName={profile?.full_name || null}
          currentAvatarPath={profile?.avatar_url || null}
          onSuccess={refreshProfile}
        />
      </div>
    </TooltipProvider>
  );
}
